import { formatCents, parseMoneyToCents, startOfDay, toDateParam, tt, type MoneyInput } from '@agos/shared'

/**
 * External drivers — couriers who are not employees and are paid per delivery.
 *
 * §9.2 said this feature was broken, and the diagnosis is in
 * `docs/pages/manager-delivery/external-driver.md`: **nothing could create an
 * external driver.** `is_external` had no write path in either backend, so this
 * screen managed a set that was always empty.
 *
 * **It has one now — corrected 2026-08-05, this still said otherwise in the
 * present tense.** `EmployeeService` names `is_external` in its `INSERT` citing
 * §9.2, assigns it on update when the body carries `isExternal`, and the
 * employee dialog posts it from a checkbox. The paragraph above promised this
 * page would work on the first driver the backend produced; §9.2's journey e2e
 * seeds one and walks it, so that promise is kept rather than pending.
 *
 * The set is still empty on the restored dump — no franchise has created one —
 * but that is now a fact about the data rather than about the capability, and
 * the two read identically on screen. That is the distinction worth keeping.
 */

export interface ExternalDriver {
  id?: number
  name?: string
  position?: string
  phone?: string | null
  email?: string | null
  active?: boolean
  isAvailable?: boolean
  emailVerified?: boolean
  adminApproved?: boolean
  ratePerOrder?: MoneyInput
  ratePerKm?: MoneyInput
  deliveryCount?: number
  totalKm?: number
  reward?: MoneyInput
  /** §9.786 - what they collected at the door on cash orders in the period, which they paid the restaurant at pickup. */
  cashCollected?: MoneyInput
}

export type Period = 'today' | 'week' | 'month' | 'custom'

/**
 * The period buttons, computed from local components.
 *
 * `toDateParam` rather than `toISOString().slice(0, 10)` — FB-034 for the fourth
 * time. Legacy's `weekStart()` also mutates the `Date` it is measuring from
 * (`new Date(d.setDate(diff))` sets `d` as a side effect), which is harmless
 * only because it is discarded immediately after.
 */
export function periodRange(
  period: Period,
  custom: { from: string; to: string },
  now: Date,
): { from: string; to: string } {
  const today = toDateParam(now)

  if (period === 'today') return { from: today, to: today }

  if (period === 'week') {
    const monday = startOfDay(now)
    const weekday = monday.getDay()
    // Sunday is day 0 and belongs to the week that started six days earlier.
    monday.setDate(monday.getDate() - (weekday === 0 ? 6 : weekday - 1))
    return { from: toDateParam(monday), to: today }
  }

  if (period === 'month') {
    const first = startOfDay(now)
    first.setDate(1)
    return { from: toDateParam(first), to: today }
  }

  return custom
}

/**
 * Where a driver is in the two-gate sign-up.
 *
 * Both gates must pass before they can log in: they confirm their own email, and
 * then an administrator approves them. `blocked` is the state the page exists to
 * clear.
 */
export type DriverGate = 'ready' | 'awaiting-approval' | 'awaiting-email' | 'blocked'

export function driverGate(driver: ExternalDriver): DriverGate {
  const verified = driver.emailVerified === true
  const approved = driver.adminApproved === true
  if (verified && approved) return 'ready'
  if (verified) return 'awaiting-approval'
  if (approved) return 'awaiting-email'
  return 'blocked'
}

/** Whether an administrator pressing *approve* would actually let them in. */
export function approvalUnblocks(driver: ExternalDriver): boolean {
  return driverGate(driver) === 'awaiting-approval'
}

export interface ExternalTotals {
  driverCount: number
  deliveries: number
  kilometres: number
  /** In cents — §14.7. This is a payout. */
  rewardCents: number
  /** §9.786 - cash the couriers collected at the door, in cents. */
  cashCents: number
}

/**
 * What the couriers earned, and what they collected.
 *
 * `reward` is the server's sum of the reward **fixed on each order** (BACKEND.md §10A.438) - the rate that applied
 * then, not the rule today - with the retired per-driver rates only as the fallback for an order from before
 * §10A.438. Summed in cents: this is money leaving the business, and *Odmeny spolu* is what the day's closing
 * records as one *Výber*.
 */
export function externalTotals(drivers: ExternalDriver[]): ExternalTotals {
  return {
    driverCount: drivers.length,
    deliveries: drivers.reduce((total, driver) => total + (driver.deliveryCount ?? 0), 0),
    kilometres: drivers.reduce((total, driver) => total + (driver.totalKm ?? 0), 0),
    rewardCents: drivers.reduce((total, driver) => total + parseMoneyToCents(driver.reward), 0),
    cashCents: drivers.reduce((total, driver) => total + parseMoneyToCents(driver.cashCollected), 0),
  }
}

/** The manager's own sign-up route for a courier, `manager-auth`'s `/kurier` - spelled once, where it is offered. */
export const COURIER_SIGNUP_PAGE = '/kurier'

/**
 * The sign-up link to send a courier - the owner's answer 7: *the driver signs up and the restaurant approves*. On
 * the manager's own address, so the account is created in this franchise.
 */
export function courierSignupUrl(origin: string): string {
  return `${origin.replace(/\/+$/, '')}${COURIER_SIGNUP_PAGE}`
}

/**
 * The balance as the report says it, from the restaurant's side - *Šofér dlží prevádzke 11,00 €* / *Prevádzka dlží
 * šoférovi 4,50 €* / *Vyrovnané*. The server's sign: positive when the courier owes (§10A.438 D).
 */
export function balanceStateNote(owesCents: number): string {
  if (owesCents > 0) return `${tt('Šofér dlží prevádzke')} ${formatCents(owesCents)}`
  if (owesCents < 0) return `${tt('Prevádzka dlží šoférovi')} ${formatCents(-owesCents)}`
  return tt('Vyrovnané')
}
